import {
	type ProethicaModuleKind,
	proethicaModuleListMeta,
} from "@constants/demo/proethica-modules";
import {
	type ProethicaTestDescriptor,
	type ProethicaTestKind,
	getProethicaModuleNumber,
	getProethicaTestDescriptor,
	injectProethicaTests,
} from "@constants/demo/proethica-tests";
import type { MixedItemTag } from "@memorang/types";
import type { TFunction } from "i18next";
import type { EXAM_STATUS } from "types/tag";
import { buttonConfig, statusMeta } from "../constants";

// Gating marks entries non-interactive via the shared `disabled` affordance
// rather than `locked` (which routes to the purchase flow).
type ProethicaListTag = MixedItemTag & { disabled?: boolean };

export const getModuleLabel = (tag: MixedItemTag) =>
	tag.label || tag.title || "";

export const getModuleMetaKey = (tag: MixedItemTag) => {
	const label = getModuleLabel(tag);
	const match = label.match(/Module\s+\d+/i);
	return match ? match[0] : label;
};

export const getProethicaModuleKind = (
	tag: MixedItemTag,
): ProethicaModuleKind => {
	const meta = proethicaModuleListMeta[getModuleMetaKey(tag)];
	return meta?.moduleKind ?? "standard";
};

const isProethicaModuleTag = (tag: MixedItemTag): boolean =>
	(tag.displayType?.includes("Display") ?? false) &&
	"distribution" in tag &&
	/^Module\s+\d+/i.test(getModuleLabel(tag));

export const getProethicaModuleTags = (
	tags: MixedItemTag[] = [],
): MixedItemTag[] => tags.filter(isProethicaModuleTag);

// The dashboard learning path: the 8 modules with the pre/post tests injected
// after their anchor modules. Advancing through this list (vs. module-number+1)
// keeps the report's "continue" in lockstep with the gating unlock model.
export const getProethicaOrderedEntries = (
	tags: MixedItemTag[] = [],
): MixedItemTag[] => injectProethicaTests(getProethicaModuleTags(tags));

export const getProethicaEntryShortLabel = (tag: MixedItemTag): string =>
	getProethicaTestDescriptor(String(tag.id))?.title ?? getModuleMetaKey(tag);

const deriveStatus = (tag: MixedItemTag): EXAM_STATUS => {
	const meta = proethicaModuleListMeta[getModuleMetaKey(tag)];
	if (meta) {
		return meta.status;
	}
	if ((tag.averageScore ?? 0) >= 85 && (tag.distribution?.none ?? 0) === 0) {
		return "COMPLETE";
	}
	if ((tag.averageScore ?? 0) > 0) {
		return "ACTIVE";
	}
	return "NOT_STARTED";
};

const resolveCta = (status: EXAM_STATUS, t: TFunction) =>
	status === "COMPLETE"
		? t("dashboard.diagnostics.viewResults")
		: (buttonConfig[status].text ?? "Start");

const getProethicaTestListProps = (
	test: ProethicaTestDescriptor,
	tag: MixedItemTag,
	t: TFunction,
	liveStatus?: EXAM_STATUS,
) => {
	const status = liveStatus ?? test.status;
	return {
		title: test.title,
		moduleTitle: test.title,
		moduleNumber: undefined,
		moduleKind: "standard" as ProethicaModuleKind,
		subtitle: test.subtitle,
		statusLabel: statusMeta[status].text,
		status,
		cta: resolveCta(status, t),
		thumbnailUrl: "",
		thumbnailSource: undefined,
		locked: false,
		disabled: (tag as ProethicaListTag).disabled ?? false,
		id: test.id,
		typeLabel: test.typeLabel,
		numItems: test.numItems,
		duration: undefined,
		durationLabel: undefined,
		isTest: true,
		testKind: test.kind as ProethicaTestKind | undefined,
		icon: test.icon as string | undefined,
	};
};

export const getProethicaModuleListProps = (
	tag: MixedItemTag,
	t: TFunction,
	liveStatus?: EXAM_STATUS,
) => {
	const test = getProethicaTestDescriptor(String(tag.id));
	if (test) {
		return getProethicaTestListProps(test, tag, t, liveStatus);
	}

	const label = getModuleLabel(tag);
	const meta = proethicaModuleListMeta[getModuleMetaKey(tag)];
	const status = liveStatus ?? deriveStatus(tag);

	const typeLabel = meta?.moduleKindLabel ?? "Lesson";
	const duration =
		meta?.durationMinutes ??
		Math.max(2, Math.round((tag.numItems ?? 10) * 0.8));
	const durationLabel = meta?.durationLabel ?? `${duration} min`;

	return {
		title: meta?.titleWithNumber ?? meta?.title ?? tag.title ?? label,
		moduleTitle: meta?.title ?? tag.title ?? label,
		moduleNumber: meta?.moduleNumber,
		moduleKind: meta?.moduleKind ?? "standard",
		subtitle: meta?.subtitle ?? `${tag.numItems ?? 0} items`,
		statusLabel: statusMeta[status].text,
		status,
		cta: resolveCta(status, t),
		thumbnailUrl: meta?.thumbnailUrl ?? "",
		thumbnailSource: meta?.thumbnailSource,
		locked: tag.locked,
		disabled: (tag as ProethicaListTag).disabled ?? meta?.disabled ?? false,
		id: String(tag.id),
		typeLabel,
		numItems: tag.numItems ?? 10,
		duration,
		durationLabel,
		isTest: false,
		testKind: undefined as ProethicaTestKind | undefined,
		icon: undefined as string | undefined,
	};
};

export type ProethicaGatingSignals = {
	preTestCompleted?: boolean;
	completedModuleNumbers?: number[];
};

const isProethicaEntryUnlocked = (
	tag: MixedItemTag,
	signals: ProethicaGatingSignals,
): boolean => {
	const module1Complete = signals.completedModuleNumbers?.includes(1) ?? false;
	const preTestDone = signals.preTestCompleted ?? false;
	const test = getProethicaTestDescriptor(String(tag.id));
	if (test) {
		return test.kind === "pretest" ? module1Complete : preTestDone;
	}
	const moduleNumber = getProethicaModuleNumber(tag);
	return moduleNumber == null || moduleNumber === 1 || preTestDone;
};

// One resolver feeding home, modules tab, and quick-study: inject the pre/post
// test rows after their anchor modules, then apply the live unlock model
// (Module 1 open, Pre-Test opens after Module 1, the rest opens once the
// Pre-Test is submitted). Under mock data (isLive=false) gating is a no-op so
// every entry stays enabled; live completion-signal wiring lands at cutover.
export const resolveProethicaModuleItems = (
	items: MixedItemTag[],
	isLive: boolean,
	signals: ProethicaGatingSignals = {},
): MixedItemTag[] => {
	const withTests = injectProethicaTests(items);
	if (!isLive) {
		return withTests;
	}
	return withTests.map((tag) =>
		isProethicaEntryUnlocked(tag, signals)
			? tag
			: ({ ...tag, disabled: true } as ProethicaListTag),
	);
};

const quickStudySectionOrder: EXAM_STATUS[] = [
	"ACTIVE",
	"NOT_STARTED",
	"COMPLETE",
];

export const getProethicaModulesGroupedByStatus = (
	tags: MixedItemTag[],
	t: TFunction,
	getLiveStatus?: (tag: MixedItemTag) => EXAM_STATUS | undefined,
) => {
	const modules = tags
		.filter((tag) => "distribution" in tag)
		.map((tag) => getProethicaModuleListProps(tag, t, getLiveStatus?.(tag)));

	return quickStudySectionOrder
		.map((status) => ({
			status,
			title: t(`dashboard.quickStudy.sections.${status}`),
			modules: modules.filter((module) => module.status === status),
		}))
		.filter((section) => section.modules.length > 0);
};
